import type { Product } from '../types/Product';
import type { ServiceResponse, PaginatedResponse, FilterOptions, PaginationOptions } from './types';
import { API_ENDPOINTS } from '../config/api.config';
import { apiRequest } from '../utils/api';
import { createDedupedRequest } from '../utils/requestDeduplication';

// Backend product response type
interface ProductResponseDto {
  _id: string;
  id: string;
  name: string;
  slug: string;
  category: string;
  brand: string;
  price: number;
  stock: number;
  status: "active" | "inactive" | "out_of_stock";
  image: string;
  sku: string;
  description: string;
  inStock: boolean;
  createdAt: string;
  updatedAt: string;
  originalPrice?: number;
  discount?: number;
  productCode?: string;
  medias?: Array<{
    type: 'image' | 'video';
    url: string;
    caption?: string;
    displayOrder?: number;
    isMain?: boolean;
  }>;
  packageType?: "Box" | "Case" | "Pieces";
  piecesPerBox?: number;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  weight?: number;
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
  tags?: string[];
  views?: number;
  salesCount?: number;
  defaultRating?: number;
}

// Backend paginated response
interface BackendPaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

class ProductService {
  // Transform backend product to frontend format
  private transformProduct(product: ProductResponseDto): Product {
    return {
      id: product._id || product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      originalPrice: product.originalPrice,
      cost: undefined, // Not exposed in API
      image: product.image,
      category: product.category,
      description: product.description,
      inStock: product.inStock,
      featured: product.tags?.some(tag => tag.toLowerCase() === 'featured') || false,
      isNew: product.tags?.some(tag => tag.toLowerCase() === 'new') || false,
      medias: product.medias,
      productCode: product.productCode,
      sku: product.sku,
      brand: product.brand,
      packSize: product.packageType,
      piecesPerBox: product.piecesPerBox,
      discount: product.discount,
      tags: product.tags
    };
  }

  // Internal method to fetch products
  private async _getProducts(
    filters?: FilterOptions,
    pagination?: PaginationOptions
  ): Promise<ServiceResponse<PaginatedResponse<Product>>> {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Always filter for active products only
      params.append('status', 'active');
      
      if (filters) {
        if (filters.category) params.append('category', filters.category);
        if (filters.brand) params.append('brand', filters.brand);
        if (filters.minPrice !== undefined) params.append('minPrice', filters.minPrice.toString());
        if (filters.maxPrice !== undefined) params.append('maxPrice', filters.maxPrice.toString());
        if (filters.inStock !== undefined) params.append('inStock', filters.inStock.toString());
        if (filters.isNew !== undefined) params.append('isNew', filters.isNew.toString());
        if (filters.search) params.append('search', filters.search);
        if (filters.tags && filters.tags.length > 0) {
          filters.tags.forEach(tag => params.append('tags', tag));
        }
        if (filters.sortBy) {
          params.append('sortBy', filters.sortBy as string);
          if (filters.sortOrder) {
            params.append('sortOrder', filters.sortOrder);
          }
          console.log('ProductService.getProducts - sortBy:', filters.sortBy); // Debug log
        }
      }
      
      if (pagination) {
        params.append('page', (pagination.page || 1).toString());
        params.append('limit', (pagination.limit || 100).toString());
        console.log('ProductService.getProducts - pagination:', pagination); // Debug log
      }
      
      const queryString = params.toString();
      const endpoint = queryString ? `${API_ENDPOINTS.PRODUCTS.BASE}?${queryString}` : API_ENDPOINTS.PRODUCTS.BASE;
      console.log('ProductService.getProducts - filters:', filters); // Debug log
      console.log('ProductService.getProducts - final endpoint:', endpoint); // Debug log
      
      const response = await apiRequest<BackendPaginatedResponse<ProductResponseDto>>(endpoint);
      
      return {
        success: true,
        data: {
          data: response.data.map(p => this.transformProduct(p)),
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages
        }
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      return {
        success: false,
        data: {
          data: [],
          total: 0,
          page: 1,
          limit: 100,
          totalPages: 0
        },
        error: error instanceof Error ? error.message : 'Failed to fetch products'
      };
    }
  }

  // Get single product by ID
  async getProductById(id: string): Promise<ServiceResponse<Product | null>> {
    try {
      const product = await apiRequest<ProductResponseDto>(API_ENDPOINTS.PRODUCTS.BY_ID(id));
      
      // Check if product is active
      if (product.status !== 'active') {
        return {
          success: false,
          data: null,
          error: 'Product not found'
        };
      }
      
      return {
        success: true,
        data: this.transformProduct(product)
      };
    } catch (error) {
      console.error('Error fetching product:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Product not found'
      };
    }
  }

  // Get single product by slug
  async getProductBySlug(slug: string): Promise<ServiceResponse<Product | null>> {
    try {
      const product = await apiRequest<ProductResponseDto>(`${API_ENDPOINTS.PRODUCTS.BASE}/slug/${slug}`);
      
      // Check if product is active
      if (product.status !== 'active') {
        return {
          success: false,
          data: null,
          error: 'Product not found'
        };
      }
      
      return {
        success: true,
        data: this.transformProduct(product)
      };
    } catch (error) {
      console.error('Error fetching product by slug:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Product not found'
      };
    }
  }

  // Get product by SKU
  async getProductBySku(sku: string): Promise<ServiceResponse<Product | null>> {
    try {
      const product = await apiRequest<ProductResponseDto>(API_ENDPOINTS.PRODUCTS.BY_SKU(sku));
      
      // Check if product is active
      if (product.status !== 'active') {
        return {
          success: false,
          data: null,
          error: 'Product not found'
        };
      }
      
      return {
        success: true,
        data: this.transformProduct(product)
      };
    } catch (error) {
      console.error('Error fetching product by SKU:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Product not found'
      };
    }
  }

  // Get featured products
  async getFeaturedProducts(): Promise<ServiceResponse<Product[]>> {
    try {
      const params = new URLSearchParams();
      params.append('tags', 'Featured');
      params.append('status', 'active');
      params.append('inStock', 'true');
      params.append('limit', '8');
      
      const endpoint = `${API_ENDPOINTS.PRODUCTS.BASE}?${params.toString()}`;
      const response = await apiRequest<BackendPaginatedResponse<ProductResponseDto>>(endpoint);
      
      return {
        success: true,
        data: response.data.map(p => this.transformProduct(p))
      };
    } catch (error) {
      console.error('Error fetching featured products:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch featured products'
      };
    }
  }

  // Get new arrivals
  async getNewArrivals(): Promise<ServiceResponse<Product[]>> {
    try {
      const params = new URLSearchParams();
      params.append('tags', 'New');
      params.append('status', 'active');
      params.append('inStock', 'true');
      params.append('sortBy', 'newest');
      params.append('limit', '10');
      
      const endpoint = `${API_ENDPOINTS.PRODUCTS.BASE}?${params.toString()}`;
      const response = await apiRequest<BackendPaginatedResponse<ProductResponseDto>>(endpoint);
      
      return {
        success: true,
        data: response.data.map(p => this.transformProduct(p))
      };
    } catch (error) {
      console.error('Error fetching new arrivals:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch new arrivals'
      };
    }
  }

  // Get products by category
  async getProductsByCategory(
    categorySlug: string,
    pagination?: PaginationOptions
  ): Promise<ServiceResponse<PaginatedResponse<Product>>> {
    try {
      const params = new URLSearchParams();
      params.append('category', categorySlug);
      params.append('status', 'active');
      
      if (pagination) {
        params.append('page', (pagination.page || 1).toString());
        params.append('limit', (pagination.limit || 100).toString());
      }
      
      const endpoint = `${API_ENDPOINTS.PRODUCTS.BASE}?${params.toString()}`;
      const response = await apiRequest<BackendPaginatedResponse<ProductResponseDto>>(endpoint);
      
      return {
        success: true,
        data: {
          data: response.data.map(p => this.transformProduct(p)),
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages
        }
      };
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return {
        success: false,
        data: {
          data: [],
          total: 0,
          page: 1,
          limit: 100,
          totalPages: 0
        },
        error: error instanceof Error ? error.message : 'Failed to fetch products by category'
      };
    }
  }

  // Search products
  async searchProducts(
    query: string,
    pagination?: PaginationOptions
  ): Promise<ServiceResponse<PaginatedResponse<Product>>> {
    try {
      const params = new URLSearchParams();
      params.append('search', query);
      params.append('status', 'active');
      
      if (pagination) {
        params.append('page', (pagination.page || 1).toString());
        params.append('limit', (pagination.limit || 100).toString());
      }
      
      const endpoint = `${API_ENDPOINTS.PRODUCTS.BASE}?${params.toString()}`;
      const response = await apiRequest<BackendPaginatedResponse<ProductResponseDto>>(endpoint);
      
      return {
        success: true,
        data: {
          data: response.data.map(p => this.transformProduct(p)),
          total: response.total,
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages
        }
      };
    } catch (error) {
      console.error('Error searching products:', error);
      return {
        success: false,
        data: {
          data: [],
          total: 0,
          page: 1,
          limit: 100,
          totalPages: 0
        },
        error: error instanceof Error ? error.message : 'Failed to search products'
      };
    }
  }

  // Get related products (by category and tags)
  async getRelatedProducts(productId: string, limit: number = 4): Promise<ServiceResponse<Product[]>> {
    try {
      // First get the product to know its category and tags
      const productResponse = await this.getProductById(productId);
      if (!productResponse.success || !productResponse.data) {
        throw new Error('Product not found');
      }
      
      const product = productResponse.data;
      const params = new URLSearchParams();
      params.append('category', product.category);
      params.append('status', 'active');
      params.append('inStock', 'true');
      params.append('limit', (limit + 1).toString()); // Get one extra to filter out current product
      
      const endpoint = `${API_ENDPOINTS.PRODUCTS.BASE}?${params.toString()}`;
      const response = await apiRequest<BackendPaginatedResponse<ProductResponseDto>>(endpoint);
      
      // Filter out the current product and limit results
      const relatedProducts = response.data
        .map(p => this.transformProduct(p))
        .filter(p => p.id !== productId)
        .slice(0, limit);
      
      return {
        success: true,
        data: relatedProducts
      };
    } catch (error) {
      console.error('Error fetching related products:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch related products'
      };
    }
  }

  // Get product categories
  async getProductCategories(): Promise<ServiceResponse<string[]>> {
    try {
      const categories = await apiRequest<string[]>(API_ENDPOINTS.PRODUCTS.CATEGORIES);
      
      return {
        success: true,
        data: categories
      };
    } catch (error) {
      console.error('Error fetching product categories:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch product categories'
      };
    }
  }

  // Get product brands
  async getProductBrands(): Promise<ServiceResponse<string[]>> {
    try {
      const brands = await apiRequest<string[]>(API_ENDPOINTS.PRODUCTS.BRANDS);
      
      return {
        success: true,
        data: brands
      };
    } catch (error) {
      console.error('Error fetching product brands:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch product brands'
      };
    }
  }
  
  // Create deduped version of getProducts
  getProducts = createDedupedRequest(
    this._getProducts.bind(this),
    (filters?: FilterOptions, pagination?: PaginationOptions) => {
      // Create a unique key based on filters and pagination
      const filterKey = filters ? JSON.stringify(filters) : 'no-filters';
      const paginationKey = pagination ? JSON.stringify(pagination) : 'no-pagination';
      return `products-${filterKey}-${paginationKey}`;
    }
  );
}

export const productService = new ProductService();